import {useCallback, useRef} from 'react'

/**
 * Handles brush painting on the mask canvas.
 * Erase mode paints black (remove), Restore mode paints white (keep).
 * Interpolates between points for smooth strokes.
 *
 * Tracks whether any paint was actually applied during a stroke so that
 * the history snapshot is only pushed for strokes that changed the mask.
 */
export function useRemoveBgBrush({getMaskContext, clientToImage, onStrokeEnd, scheduleRender}) {
    const isDrawingRef = useRef(false)
    const lastPointRef = useRef(null)
    const didPaintRef = useRef(false)

    /**
     * Paint a circle on the mask at image coordinates.
     */
    const paintAt = useCallback(
        (x, y, brushSize, mode) => {
            const ctx = getMaskContext()
            if (!ctx) return

            if (mode === 'erase') {
                ctx.globalCompositeOperation = 'destination-out'
                ctx.fillStyle = '#000000'
            } else {
                ctx.globalCompositeOperation = 'source-over'
                ctx.fillStyle = 'rgba(255,255,255,1)'
            }
            ctx.beginPath()
            ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2)
            ctx.fill()
            ctx.globalCompositeOperation = 'source-over'
        },
        [getMaskContext],
    )

    /**
     * Interpolate between two points and paint along the line.
     */
    const paintLine = useCallback(
        (from, to, brushSize, mode) => {
            const dx = to.x - from.x
            const dy = to.y - from.y
            const dist = Math.sqrt(dx * dx + dy * dy)
            const step = Math.max(1, brushSize / 4)
            const steps = Math.ceil(dist / step)

            for (let i = 0; i <= steps; i++) {
                const t = steps === 0 ? 0 : i / steps
                const x = from.x + dx * t
                const y = from.y + dy * t
                paintAt(x, y, brushSize, mode)
            }
        },
        [paintAt],
    )

    const startStroke = useCallback(
        (e, brushSize, mode) => {
            const pt = clientToImage(e.clientX, e.clientY)
            if (!pt) return

            isDrawingRef.current = true
            didPaintRef.current = true
            lastPointRef.current = pt
            paintAt(pt.x, pt.y, brushSize, mode)
            scheduleRender()
        },
        [clientToImage, paintAt, scheduleRender],
    )

    const moveStroke = useCallback(
        (e, brushSize, mode) => {
            if (!isDrawingRef.current) return

            const pt = clientToImage(e.clientX, e.clientY)
            if (!pt) return

            if (lastPointRef.current) {
                paintLine(lastPointRef.current, pt, brushSize, mode)
            } else {
                paintAt(pt.x, pt.y, brushSize, mode)
            }
            lastPointRef.current = pt
            scheduleRender()
        },
        [clientToImage, paintAt, paintLine, scheduleRender],
    )

    const endStroke = useCallback(() => {
        if (!isDrawingRef.current) return
        isDrawingRef.current = false
        lastPointRef.current = null
        if (didPaintRef.current) {
            onStrokeEnd?.()
        }
        didPaintRef.current = false
    }, [onStrokeEnd])

    return {
        startStroke,
        moveStroke,
        endStroke,
        isDrawing: isDrawingRef,
    }
}
